import { useCallback, useEffect, useState } from "react";
import { Link, Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Building2, CheckCircle2, ChevronLeft, ChevronRight, Download, Keyboard, Loader2, PenLine, RefreshCw, Search } from "lucide-react";
import PageFrame from "@/components/PageFrame";
import OwnershipModal from "@/components/OwnershipModal";
import NatureSearch from "@/components/NatureSearch";
import InvoiceDetails from "@/components/InvoiceDetails";
import { emptyInvoice, invoiceComplete, invoicePayload } from "@/data/invoice";
import { api } from "@/api";
import { useAuth } from "@/auth";
import { LAYOUT } from "@/data/affidavits";
import { MIN_QSE_BLACK, isQse } from "@/data/ownership";
import {
  EMPTY_DETAILS,
  ENTITY_TYPES,
  MAX_LEN,
  NOT_APPLICABLE,
  PROVINCES,
  detailsPayload,
  detailsProblems,
  formatRegistration,
  isSpecialised,
  nameInitials,
  tidyPersonName,
  typePersonName,
} from "@/data/details";

// Company Details (owner, 2026-10-10): between the black ownership choice and the affidavit. The user
// types page 1's details, or picks the company from Xero, which fills them in; then the Invoice Details
// (InvoiceDetails.jsx). The Next button only shows once everything that is needed is filled in correctly.

export const METHOD_MANUAL = "manual";
export const METHOD_XERO = "xero";

// From Xero these stay open: Xero doesn't hold the deponent's ID number, the Nature of Business (SIC 7
// list) or the Specialised affidavits' Type of Entity; the deponent's name is prefilled but can change
const ALWAYS_OPEN = ["deponent_name", "id_number", "nature_of_business", "entity_type", "entity_type_other"];
const COMPANY_FIELDS = Object.keys(EMPTY_DETAILS).filter((k) => !["id_number", "entity_type", "entity_type_other"].includes(k));

const digitsOnly = (v, max) => v.replace(/\D/g, "").slice(0, max);

const MethodCard = ({ icon: Icon, title, text, selected, onClick, testId }) => (
  <button
    type="button"
    className={`bee-comm-option bee-cd-method${selected ? " is-selected" : ""}`}
    onClick={onClick}
    aria-pressed={selected}
    data-testid={testId}
  >
    <span className="bee-cd-method-head">
      <Icon className="bee-cd-method-icon" />
      <span className="bee-comm-option-name">{title}</span>
    </span>
    <span className="bee-comm-option-title">{text}</span>
    {selected && <CheckCircle2 className="bee-comm-option-check" />}
  </button>
);

const Field = ({ id, label, required, optional, error, hint, ok, children }) => (
  <div className="bee-cd-field">
    <label className="bee-cd-label" htmlFor={id}>
      {label}
      {required && <span className="bee-cd-required" aria-hidden="true"> *</span>}
      {optional && <span className="bee-cd-optional"> (optional)</span>}
    </label>
    {children}
    {error ? (
      <p className="bee-field-hint is-error" data-testid={`error-${id}`}>{error}</p>
    ) : ok ? (
      <p className="bee-cd-ok" data-testid={`ok-${id}`}><CheckCircle2 size={15} /> {ok}</p>
    ) : (
      hint && <p className="bee-field-hint">{hint}</p>
    )}
  </div>
);

// A green message that counts down at its right-hand end and then goes, so what is below moves up
// (owner, 2026-10-11: Find in Xero's "Valid South African ID Number" and "Found in Xero: ...", 10 seconds)
const FOUND_SECONDS = 10;

// warn: red instead (owner, 2026-10-11: "No contact with an ID number was found in Xero ..." - also 10 s)
const TimedOk = ({ text, onDone, testId, warn, seconds = FOUND_SECONDS }) => {
  const [left, setLeft] = useState(seconds);
  useEffect(() => {
    const started = Date.now();
    const tick = setInterval(() => {
      const rest = seconds - Math.floor((Date.now() - started) / 1000);
      if (rest <= 0) {
        clearInterval(tick);
        onDone();
      } else {
        setLeft(rest);
      }
    }, 250);
    return () => clearInterval(tick);
  }, [seconds, onDone]);
  return (
    <p className={warn ? "bee-field-hint is-error bee-xero-note" : "bee-cd-ok"} role={warn ? "alert" : "status"} data-testid={testId}>
      {!warn && <CheckCircle2 size={15} />} <span>{text}</span>
      <span className="bee-xero-note-timer" aria-hidden="true">{left}s</span>
    </p>
  );
};

// The fields: the same for both ways; from Xero the company's fields are locked (greyed out) until
// Make Changes (ALWAYS_OPEN stay open). revealErrors: after Load Client, what Xero didn't fill in
// correctly shows red straight away (Make Changes to fix it). idAction / idNote: Find in Xero (beside the
// ID number) and its result. nameFromXero: the deponent's name was filled in from Xero (Load Client, the
// cycle arrows, Find in Xero) and not changed since - its problems show straight away.
const DetailsFields = ({ slug, values, onChange, locked, revealErrors, prefix, idAction, idNote, onIdNoteDone, nameAction, nameFromXero }) => {
  const specialised = isSpecialised(slug);
  const [touched, setTouched] = useState({});
  const [tradingAuto, setTradingAuto] = useState(!values.trading_name || values.trading_name === values.enterprise_name);
  const problems = detailsProblems(values, slug);
  const isLocked = (k) => locked && !ALWAYS_OPEN.includes(k);
  const touch = (k) => setTouched((t) => ({ ...t, [k]: true }));
  const errorFor = (k) => (touched[k] || (revealErrors && isLocked(k)) ? problems[k] || "" : "");
  const set = (k, v) => onChange({ ...values, [k]: v });
  const id = (k) => `${prefix}-${k}`;

  const input = (k, props = {}) => (
    <input
      id={id(k)}
      className={`bee-input${errorFor(k) ? " is-invalid" : ""}`}
      value={values[k]}
      onChange={(e) => set(k, e.target.value.slice(0, MAX_LEN[k]))}
      onBlur={() => touch(k)}
      disabled={isLocked(k)}
      autoComplete="off"
      data-testid={`input-${k}`}
      {...props}
    />
  );

  // Owner: the Trading Name follows the Enterprise Name (saves typing) until the user changes it
  const enterpriseBlur = () => {
    touch("enterprise_name");
    if ((tradingAuto || !values.trading_name.trim()) && values.enterprise_name.trim()) {
      onChange({ ...values, trading_name: values.enterprise_name.replace(/\s+/g, " ").trim() });
      setTradingAuto(true);
    }
  };

  // Full name & Surname (owner, 2026-10-11, as on the CIPC site): proper case once the field is left; at
  // least a name and a surname, no initials. A name from Xero with an initial ("J. Smith"): the user is
  // asked to replace the initial with the full name.
  const xeroInitials = nameFromXero ? nameInitials(values.deponent_name) : [];
  const nameError = touched.deponent_name || nameFromXero
    ? (xeroInitials.length
      ? (xeroInitials.length > 1
        ? `The name from Xero has initials (${xeroInitials.join(", ")}): please replace them with the full name, as on the ID document.`
        : `The name from Xero has an initial (${xeroInitials[0]}): please replace it with the full name, as on the ID document.`)
      : problems.deponent_name || "")
    : "";
  const nameBlur = () => {
    touch("deponent_name");
    const tidy = tidyPersonName(values.deponent_name);
    if (tidy !== values.deponent_name) set("deponent_name", tidy);
  };

  const idValid = values.id_number && !problems.id_number;
  // Find in Xero filled in this ID number: its two green messages count down 10 seconds, then go (owner)
  const foundId = idNote?.forId && idNote.forId === values.id_number;
  const idTimed = foundId && idNote.timed;
  const idQuiet = foundId && idNote.gone;

  return (
    <div className={`bee-cd-fields${locked ? " is-locked" : ""}`}>
      <h3 className="bee-cd-group">Deponent (the person signing the affidavit)</h3>
      <Field id={id("deponent_name")} label="Full name & Surname" required error={nameError}>
        <div className="bee-cd-name-row">
          {input("deponent_name", {
            placeholder: "e.g. Thabo Mokoena",
            autoComplete: "name",
            className: `bee-input${nameError ? " is-invalid" : ""}`,
            // Letters, spaces, hyphens and apostrophes only: full stops, colons, semicolons go as they're typed
            onChange: (e) => set("deponent_name", typePersonName(e.target.value).slice(0, MAX_LEN.deponent_name)),
            onBlur: nameBlur,
          })}
          {nameAction}
        </div>
      </Field>
      <Field
        id={id("id_number")}
        label="Identity Number"
        required
        // As on the Personal Tax site: checked when the field is left, and as soon as all 13 digits are in
        error={touched.id_number || values.id_number.length === 13 ? problems.id_number || "" : ""}
        ok={idValid && !idTimed && !idQuiet ? "Valid South African ID Number" : ""}
      >
        <div className="bee-cd-id-row">
          {input("id_number", {
            placeholder: "13-digit South African ID number",
            inputMode: "numeric",
            onChange: (e) => set("id_number", digitsOnly(e.target.value, 13)),
          })}
          {idAction}
        </div>
        {idTimed ? (
          <>
            {idValid && <TimedOk key={`valid-${idNote.at}`} text="Valid South African ID Number" onDone={onIdNoteDone} testId="ok-id-timed" />}
            <TimedOk key={`found-${idNote.at}`} text={idNote.text} onDone={onIdNoteDone} testId="find-note" />
          </>
        ) : idNote?.timedWarn ? (
          <TimedOk key={`missing-${idNote.at}`} warn text={idNote.text} onDone={onIdNoteDone} testId="find-note" />
        ) : idNote && !idNote.gone && (
          <p className={idNote.ok ? "bee-cd-ok" : "bee-field-hint is-error"} data-testid="find-note">
            {idNote.ok && <CheckCircle2 size={15} />} {idNote.text}
          </p>
        )}
      </Field>

      <h3 className="bee-cd-group">Enterprise</h3>
      <Field id={id("enterprise_name")} label="Enterprise Name" required error={errorFor("enterprise_name")}>
        {input("enterprise_name", { placeholder: "Registered name, e.g. Mokoena Holdings (Pty) Ltd", onBlur: enterpriseBlur })}
      </Field>
      <Field
        id={id("trading_name")}
        label="Trading Name"
        required
        error={errorFor("trading_name")}
        hint="Filled in from the Enterprise Name. Change it if the business trades under another name."
      >
        {input("trading_name", {
          placeholder: "Name the business trades under",
          onChange: (e) => { setTradingAuto(false); set("trading_name", e.target.value.slice(0, MAX_LEN.trading_name)); },
        })}
      </Field>
      <Field id={id("registration_number")} label="Registration Number" required error={errorFor("registration_number")} hint="CIPC registration number, e.g. 2015/123456/07.">
        {input("registration_number", {
          placeholder: "YYYY/NNNNNN/NN",
          inputMode: "numeric",
          onChange: (e) => set("registration_number", formatRegistration(e.target.value)),
        })}
      </Field>
      {!specialised && (
        <Field
          id={id("vat_number")}
          label="VAT Number"
          optional
          error={errorFor("vat_number")}
          hint={`Leave it blank if the business is not VAT registered: the affidavit then shows "${NOT_APPLICABLE}".`}
        >
          {values.income_tax_number && !values.vat_number
            // Owner: from Xero, an Income Tax number (starts with 9) in the Tax field = not VAT registered
            // ("Not applicable" on the affidavit); typing a VAT number (after Make Changes) replaces it
            ? input("vat_number", {
              value: `Not VAT Registered (Tax# ${values.income_tax_number})`,
              className: "bee-input bee-cd-not-vat",
              onFocus: (e) => e.target.select(),
              onChange: (e) => {
                const typed = e.target.value.replace(`Not VAT Registered (Tax# ${values.income_tax_number})`, "");
                onChange({ ...values, income_tax_number: "", vat_number: digitsOnly(typed, 10) });
              },
            })
            : input("vat_number", {
              placeholder: "10 digits, starting with 4",
              inputMode: "numeric",
              onChange: (e) => set("vat_number", digitsOnly(e.target.value, 10)),
            })}
        </Field>
      )}
      {specialised && (
        <Field id={id("entity_type")} label="Type of Entity" required error={errorFor("entity_type") || errorFor("entity_type_other")}>
          <select
            id={id("entity_type")}
            className={`bee-input bee-cd-select${values.entity_type ? "" : " is-placeholder"}${errorFor("entity_type") ? " is-invalid" : ""}`}
            value={values.entity_type}
            onChange={(e) => set("entity_type", e.target.value)}
            onBlur={() => touch("entity_type")}
            disabled={isLocked("entity_type")}
            data-testid="input-entity_type"
          >
            <option value="">Choose the type of entity</option>
            {ENTITY_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
          {values.entity_type === "Other" && (
            <div className="bee-cd-gap">
              {input("entity_type_other", { placeholder: "Type of entity", "aria-label": "Type of entity (other)" })}
            </div>
          )}
        </Field>
      )}
      <Field
        id={id("nature_of_business")}
        label="Nature of Business"
        required
        error={errorFor("nature_of_business")}
        hint="Type a word and choose from South Africa's Standard Industrial Classification (SIC 7)."
      >
        <NatureSearch
          id={id("nature_of_business")}
          value={values.nature_of_business}
          onChange={(v) => set("nature_of_business", v)}
          onBlur={() => touch("nature_of_business")}
          invalid={!!errorFor("nature_of_business")}
        />
      </Field>

      <Field
        id={id("street")}
        label={specialised ? "Enterprise Physical Address" : "Enterprise Address"}
        required
        error={["street", "suburb", "city", "province", "postal_code"].map(errorFor).find(Boolean)}
      >
        <div className="bee-cd-address">
          {input("street", { placeholder: "Street number and street name", autoComplete: "address-line1" })}
          {input("suburb", { placeholder: "Suburb", "aria-label": "Suburb" })}
          {input("city", { placeholder: "City", "aria-label": "City", autoComplete: "address-level2" })}
          <select
            id={id("province")}
            className={`bee-input bee-cd-select${values.province ? "" : " is-placeholder"}${errorFor("province") ? " is-invalid" : ""}`}
            value={values.province}
            onChange={(e) => set("province", e.target.value)}
            onBlur={() => touch("province")}
            disabled={isLocked("province")}
            aria-label="Province"
            data-testid="input-province"
          >
            <option value="">Province</option>
            {PROVINCES.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          {input("postal_code", {
            placeholder: "Postal",
            "aria-label": "Postal code",
            inputMode: "numeric",
            className: `bee-input bee-cd-postal${errorFor("postal_code") ? " is-invalid" : ""}`,
            onChange: (e) => set("postal_code", digitsOnly(e.target.value, 4)),
          })}
        </div>
      </Field>
    </div>
  );
};

// The Xero section's messages (owner, 2026-10-10): the green success note in italics and the "missing
// in Xero" warning in bold disappear after 5 seconds, counting down at the end of the sentence (what is
// below then moves up); other errors stay until the next try. One line where the screen is wide enough.
const NOTE_SECONDS = 5;

const XeroNote = ({ note, onDone }) => {
  const timed = note.kind !== "error";
  const [left, setLeft] = useState(NOTE_SECONDS);
  useEffect(() => {
    if (!timed) return undefined;
    setLeft(NOTE_SECONDS);
    const started = Date.now();
    const tick = setInterval(() => {
      const rest = NOTE_SECONDS - Math.floor((Date.now() - started) / 1000);
      if (rest <= 0) {
        clearInterval(tick);
        onDone();
      } else {
        setLeft(rest);
      }
    }, 250);
    return () => clearInterval(tick);
  }, [note, timed, onDone]);
  const cls = note.kind === "success" ? "bee-cd-ok bee-xero-note is-success"
    : `bee-field-hint is-error bee-xero-note${note.kind === "warn" ? " is-warn" : ""}`;
  return (
    <p className={cls} role={note.kind === "success" ? "status" : "alert"} data-testid="xero-note">
      {note.kind === "success" && <CheckCircle2 size={15} />}
      <span className="bee-xero-note-text">{note.text}</span>
      {timed && <span className="bee-xero-note-timer" aria-hidden="true" data-testid="xero-note-timer">{left}s</span>}
    </p>
  );
};

// Find in Xero: several people match the name - the user picks (ID numbers partly hidden)
const PeopleModal = ({ matches, onPick, onCancel }) => (
  <div className="modal-overlay" data-testid="people-dialog" onClick={onCancel}>
    <div className="modal-content bee-comm-modal" onClick={(e) => e.stopPropagation()}>
      <h2>Choose the Deponent</h2>
      <p className="modal-description">These contacts in TC Auditors' Xero match the name. Click the right person.</p>
      <div className="bee-comm-options">
        {matches.map((m) => (
          <button key={m.id} type="button" className="bee-comm-option" onClick={() => onPick(m)} data-testid={`person-${m.id}`}>
            <span className="bee-comm-option-name">{m.name}</span>
            <span className="bee-comm-option-title">ID number {m.id_hint}</span>
          </button>
        ))}
      </div>
      <div className="modal-actions">
        <button type="button" className="bee-btn-plain" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  </div>
);

// Without the company's details (loading failed)
const clearCompany = (values) => ({ ...values, ...Object.fromEntries(COMPANY_FIELDS.map((k) => [k, ""])) });
// Another organisation chosen (owner, 2026-10-11): every field empty - also the deponent's ID number and the
// Type of Entity - so Load Client clearly fills in fresh values for this organisation only
const clearAll = () => ({ ...EMPTY_DETAILS });

// Select company from Xero (owner, 2026-10-10): Refresh List loads the organisations (TC Auditors' Xero
// contact group MONTHLY RETAINERS); Load Client (greyed out until the list has loaded) fills in the
// chosen one's details from its Xero contact. Make Changes only appears once they were filled in.
const XeroSection = ({ slug, xero, onChange }) => {
  const [busy, setBusy] = useState(""); // "" | "list" | "client" | "person"
  const [note, setNote] = useState(null); // {kind: success | warn | error, text}
  const [findNote, setFindNote] = useState(null); // under the ID number: {ok, text}
  const [people, setPeople] = useState(null); // several matches: choose one
  const { expired } = useAuth();
  const listLoaded = xero.organisations.length > 0;
  const clearNote = useCallback(() => setNote(null), []);
  // Find in Xero's timed messages are done: they stay away while this ID number stays as it is
  const findNoteDone = useCallback(() => setFindNote((n) => (
    n?.timed ? { gone: true, forId: n.forId } : n?.timedWarn ? null : n)), []);

  const failed = (err) => {
    if (err.status === 401) return expired();
    setNote({ kind: "error", text: err.message });
    return undefined;
  };

  // Find in Xero: the deponent's own contact (full legal name + ID number in its registration number field)
  const pickPerson = async (match) => {
    setPeople(null);
    setBusy("person");
    try {
      const p = await api(`/xero/people/${match.id}`);
      onChange({ ...xero, nameFromXero: true, values: { ...xero.values, deponent_name: tidyPersonName(p.name), id_number: p.id_number } });
      setFindNote({ ok: true, text: `Found in Xero: ${p.name}.`, timed: true, forId: p.id_number, at: Date.now() });
    } catch (err) {
      setFindNote({ ok: false, text: err.message });
    } finally {
      setBusy("");
    }
  };

  const findPerson = async () => {
    setBusy("person");
    setFindNote(null);
    let matches = [];
    try {
      matches = (await api(`/xero/people?${new URLSearchParams({ name: xero.values.deponent_name })}`)).matches;
    } catch (err) {
      setFindNote({ ok: false, text: err.message });
      setBusy("");
      return;
    }
    setBusy("");
    if (matches.length === 0) {
      setFindNote({ ok: false, timedWarn: true, at: Date.now(),
        text: `No contact with an ID number was found in Xero for "${xero.values.deponent_name}". Check the name, or type the ID number.` });
    } else if (matches.length === 1) {
      pickPerson(matches[0]);
    } else {
      setPeople(matches);
    }
  };

  const findButton = xero.filled ? (
    <button
      type="button"
      className="bee-btn-green"
      onClick={findPerson}
      disabled={!!busy || !xero.values.deponent_name.trim()}
      title={!xero.values.deponent_name.trim() ? "Type the deponent's name first" : "Find the deponent's ID number in TC Auditors' Xero"}
      data-testid="button-xero-find-person"
    >
      {busy === "person" ? <Loader2 size={16} className="bee-spin" /> : <Search size={16} />} <span>Find in Xero</span>
    </button>
  ) : null;

  // Cycle through the contact's people (owner, 2026-10-11: the primary person is often the admin person,
  // not the director). Only when the contact has more than one; it goes round and round - past the last
  // back to the first, and before the first to the last. Another person: their ID number is looked up afresh.
  const contactPeople = xero.people || [];
  const cycle = (step) => {
    const n = contactPeople.length;
    const i = (((xero.personIndex || 0) + step) % n + n) % n;
    setFindNote(null);
    onChange({ ...xero, personIndex: i, nameFromXero: true, values: { ...xero.values, deponent_name: tidyPersonName(contactPeople[i]), id_number: "" } });
  };
  const cycleButtons = xero.filled && contactPeople.length > 1 ? (
    <div className="bee-cd-cycle" data-testid="person-cycle">
      <button type="button" className="bee-btn-green bee-cd-cycle-btn" onClick={() => cycle(-1)} disabled={!!busy}
        aria-label="Previous contact person" title="Previous contact person" data-testid="button-person-prev">
        <ChevronLeft size={18} />
      </button>
      <button type="button" className="bee-btn-green bee-cd-cycle-btn" onClick={() => cycle(1)} disabled={!!busy}
        aria-label="Next contact person" title="Next contact person" data-testid="button-person-next">
        <ChevronRight size={18} />
      </button>
      <span className="bee-cd-cycle-note">cycle through contacts</span>
    </div>
  ) : null;

  const refresh = async () => {
    setBusy("list");
    setNote(null);
    try {
      const data = await api("/xero/clients");
      const still = data.organisations.some((o) => o.id === xero.orgId);
      onChange(still ? { ...xero, organisations: data.organisations }
        : { ...xero, organisations: data.organisations, orgId: "", filled: false, editing: false, people: [], personIndex: 0, nameFromXero: false, values: clearAll() });
      if (!still) setFindNote(null);
      setNote({ kind: "success", text: `${data.organisations.length} organisations loaded from the ${data.group} contact group in Xero.` });
    } catch (err) {
      failed(err);
    } finally {
      setBusy("");
    }
  };

  const load = async () => {
    setBusy("client");
    setNote(null);
    try {
      const data = await api(`/xero/clients/${xero.orgId}`);
      // The deponent's ID number, Nature of Business and Type of Entity aren't in Xero: kept as they are
      // The deponent's name in proper case, full stops gone (an initial is then pointed out under the field)
      const values = { ...clearCompany(xero.values), ...data.details, deponent_name: tidyPersonName(data.details.deponent_name) };
      // Everyone on the contact (primary person first): the arrows beside the name cycle through them
      onChange({ ...xero, filled: true, editing: false, people: data.people || [], personIndex: 0, nameFromXero: !!values.deponent_name, values });
      const incomplete = Object.keys(detailsProblems(values, slug)).some((k) => !ALWAYS_OPEN.includes(k));
      setFindNote(null);
      setNote(incomplete
        ? { kind: "warn", text: "Some details are missing or not right in Xero (shown in red): click Make Changes to complete them." }
        : { kind: "success", text: "Filled in from Xero. Click Make Changes if anything is out of date." });
    } catch (err) {
      // Owner: if the call fails, the fields stay greyed out and locked (no Make Changes)
      onChange({ ...xero, filled: false, editing: false, people: [], personIndex: 0, nameFromXero: false, values: clearCompany(xero.values) });
      failed(err);
    } finally {
      setBusy("");
    }
  };

  const choose = (orgId) => {
    // Another organisation: its details only come with Load Client (and the old Find in Xero result goes)
    setFindNote(null);
    onChange({ ...xero, orgId, filled: false, editing: false, people: [], personIndex: 0, nameFromXero: false, values: clearAll() });
  };

  return (
    <>
      <div className="bee-cd-field">
        <label className="bee-cd-label" htmlFor="xero-organisation">Xero Organisation<span className="bee-cd-required" aria-hidden="true"> *</span></label>
        <div className="bee-cd-xero-row">
          <select
            id="xero-organisation"
            className={`bee-input bee-cd-select bee-cd-xero-select${xero.orgId ? "" : " is-placeholder"}`}
            value={xero.orgId}
            onChange={(e) => choose(e.target.value)}
            disabled={!listLoaded || !!busy}
            data-testid="select-xero-organisation"
          >
            <option value="">
              {listLoaded ? "Choose the Xero organisation" : "Click Refresh List to load the Xero organisations"}
            </option>
            {xero.organisations.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
          <button type="button" className="bee-btn-green" onClick={refresh} disabled={!!busy} data-testid="button-xero-refresh">
            <RefreshCw size={16} className={busy === "list" ? "bee-spin" : ""} /> <span>Refresh List</span>
          </button>
        </div>
        {/* Owner: the message under the dropdown; Load Client below it moves up when it disappears */}
        {note && <XeroNote note={note} onDone={clearNote} />}
        <div className="bee-cd-xero-actions">
          <button
            type="button"
            className="bee-btn-green"
            onClick={load}
            disabled={!listLoaded || !xero.orgId || !!busy}
            title={!listLoaded ? "Click Refresh List first" : !xero.orgId ? "Choose the Xero organisation first" : undefined}
            data-testid="button-xero-load-client"
          >
            {busy === "client" ? <Loader2 size={16} className="bee-spin" /> : <Download size={16} />} <span>Load Client</span>
          </button>
          {xero.filled && !xero.editing && (
            <button type="button" className="bee-btn-green" onClick={() => onChange({ ...xero, editing: true })} data-testid="button-xero-make-changes">
              <PenLine size={16} /> <span>Make Changes</span>
            </button>
          )}
        </div>
      </div>

      <DetailsFields
        slug={slug}
        values={xero.values}
        // The user changing the name: it's theirs now, no longer Xero's
        onChange={(values) => onChange({ ...xero, values,
          nameFromXero: values.deponent_name === xero.values.deponent_name ? xero.nameFromXero : false })}
        nameFromXero={!!xero.nameFromXero}
        locked={!xero.editing}
        revealErrors={xero.filled}
        prefix="xero"
        idAction={findButton}
        nameAction={cycleButtons}
        idNote={findNote}
        onIdNoteDone={findNoteDone}
      />

      {people && <PeopleModal matches={people} onPick={pickPerson} onCancel={() => setPeople(null)} />}
    </>
  );
};

export const emptyForm = () => ({
  method: null,
  manual: { ...EMPTY_DETAILS },
  xero: { organisations: [], orgId: "", filled: false, editing: false, people: [], personIndex: 0, nameFromXero: false, values: { ...EMPTY_DETAILS } },
  invoice: emptyInvoice(),
});

const CompanyDetailsPage = ({ slug, info }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [ownership, setOwnership] = useState(() => {
    const o = location.state?.ownership;
    return o && !(isQse(slug) && o.black < MIN_QSE_BLACK) ? o : null;
  });
  const [form, setForm] = useState(() => ({ ...emptyForm(), ...location.state?.form }));
  const { method } = form;
  const updateInvoice = useCallback((fn) => setForm((f) => ({ ...f, invoice: fn(f.invoice) })), []);

  const active = method === METHOD_MANUAL ? form.manual : method === METHOD_XERO ? form.xero.values : null;
  // From Xero the invoice is made out to the organisation loaded from the list
  const xeroOrg = form.xero.filled && form.xero.organisations.find((o) => o.id === form.xero.orgId);
  const xeroContact = xeroOrg ? { id: xeroOrg.id, name: xeroOrg.name } : null;
  const complete =
    !!active &&
    (method === METHOD_MANUAL || (form.xero.orgId && form.xero.filled)) &&
    Object.keys(detailsProblems(active, slug)).length === 0 &&
    invoiceComplete(form.invoice, method, active.enterprise_name);

  const [blockedNote, setBlockedNote] = useState("");
  const [checking, setChecking] = useState(false);

  const next = async () => {
    // Owner: no affidavit for a client TC Auditors no longer serves (Settings → blocked organisations) -
    // also when typed in by hand (the backend checks again when sending)
    setChecking(true);
    setBlockedNote("");
    try {
      const p = detailsPayload(active, slug);
      const q = new URLSearchParams({ name: p.enterprise_name, trading: p.trading_name });
      const res = await api(`/xero/blocked/check?${q}`);
      if (res.blocked) {
        setBlockedNote(res.message);
        return;
      }
    } catch (err) {
      setBlockedNote(err.message);
      return;
    } finally {
      setChecking(false);
    }
    // This page keeps what was typed (browser Back, or Change on the affidavit page)
    navigate(location.pathname, { replace: true, state: { ownership, form } });
    navigate(`/affidavit/${slug}`, {
      state: { ownership, form, details: detailsPayload(active, slug), source: method,
        invoice: invoicePayload(form.invoice, method, xeroContact) },
    });
  };

  // At the top, and again at the bottom above "Signed in as" (owner, 2026-10-11)
  const back = (place) => (
    <Link to="/" className="btn-shimmer-orange bee-back-btn" data-testid={place === "top" ? "link-back-affidavits" : "link-back-affidavits-bottom"}>
      <ArrowLeft className="w-5 h-5" /> <span>Back to Affidavits</span>
    </Link>
  );

  return (
    <PageFrame title="B-BBEE Sworn Affidavit" subtitle={info.name} bottom={back("bottom")}>
      <div className="bee-sign-top">{back("top")}</div>

      <section className="bee-cd-section" aria-labelledby="cd-title">
        <h2 id="cd-title" className="bee-cd-title">Company Details</h2>
        <p className="bee-cd-intro">
          These details are filled in on page 1 of the affidavit. How would you like to fill them in?
        </p>
        <div className="bee-cd-methods">
          <MethodCard
            icon={Keyboard}
            title="Manually input company details"
            text="Type in the deponent's and the company's details."
            selected={method === METHOD_MANUAL}
            onClick={() => setForm((f) => ({ ...f, method: METHOD_MANUAL }))}
            testId="method-manual"
          />
          <MethodCard
            icon={Building2}
            title="Select company from Xero"
            text="Choose the company from Xero: its details are filled in for you."
            selected={method === METHOD_XERO}
            onClick={() => setForm((f) => ({ ...f, method: METHOD_XERO }))}
            testId="method-xero"
          />
        </div>
      </section>

      {method && (
        <section className="bee-cd-section" data-testid={`section-${method}`}>
          <h2 className="bee-cd-title">{method === METHOD_MANUAL ? "Enter the details" : "Select company from Xero"}</h2>
          {method === METHOD_MANUAL ? (
            <DetailsFields
              key="manual"
              slug={slug}
              values={form.manual}
              onChange={(manual) => setForm((f) => ({ ...f, manual }))}
              prefix="manual"
            />
          ) : (
            <XeroSection key="xero" slug={slug} xero={form.xero} onChange={(xero) => setForm((f) => ({ ...f, xero }))} />
          )}
        </section>
      )}

      {method && (
        <InvoiceDetails
          method={method}
          enterpriseName={active.enterprise_name}
          xeroContact={xeroContact}
          invoice={form.invoice}
          onUpdate={updateInvoice}
        />
      )}

      {method && (
        <div className="bee-cd-next">
          {complete ? (
            <div className="bee-cd-next-go">
              {blockedNote && <p className="bee-field-hint is-error" role="alert" data-testid="blocked-note">{blockedNote}</p>}
              <button type="button" className="btn-shimmer-orange bee-back-btn" onClick={next} disabled={checking} data-testid="button-next">
                <span>Next</span> {checking ? <Loader2 className="w-5 h-5 bee-spin" /> : <ArrowRight className="w-5 h-5" />}
              </button>
            </div>
          ) : (
            <p className="bee-cd-next-note" data-testid="next-note">
              The <strong>Next</strong> button appears once all the required fields (*) are filled in correctly and the
              Invoice Details are answered.
            </p>
          )}
        </div>
      )}

      {!ownership && (
        <OwnershipModal
          slug={slug}
          name={info.name}
          // Without a choice the affidavit can't be filled in: Cancel goes back to the front page
          onCancel={() => navigate("/")}
          onDone={(o) => {
            setOwnership(o);
            navigate(location.pathname, { replace: true, state: { ...location.state, ownership: o } });
          }}
        />
      )}
    </PageFrame>
  );
};

const CompanyDetails = () => {
  const { slug } = useParams();
  const info = LAYOUT[slug];
  if (!info) return <Navigate to="/" replace />;
  return <CompanyDetailsPage key={slug} slug={slug} info={info} />;
};

export default CompanyDetails;
